import { queryClient } from '@/router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { NudgingSubscriptionBanner } from '@/pages/ui/nudging-subscribe-banner/nudging-subscribe-banner'

import { createCasesMutation } from '@/lib/queries/business/v1/case/bulk/register/mutations'
import type { ResCreateCases } from '@/lib/queries/business/v1/case/types'
import { searchApplicantQueryKey } from '@/lib/queries/business/v2/applicant/search/queries'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'
import { isWretchError } from '@/lib/types/predicate'
import { cn } from '@/lib/utils'

import { COLUMN_DEFINITIONS } from '../../constants'
import { prepareTags } from '../../utils'
import { useFileContents } from './hooks/use-file-contents'
import { ImportConfirm } from './import-confirm'
import { ImportContentHeader } from './import-content-header'
import { ImportFileInput } from './import-file-input'
import { ImportPreview } from './import-preview'
import { ImportResult } from './import-result'
import paperGraphicImage from './paper-graphic.svg'

export const ImportContent = () => {
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<ResCreateCases | null>(null)
  const { hasError, rows, nInvalidRows } = useFileContents(file)
  const mutation = useMutation(createCasesMutation)

  const { data: dataUser } = useQuery(getUserQuery)

  const handleSubmit = useCallback(() => {
    mutation.mutate(
      rows.map((row) => ({
        applicantEmail: row[COLUMN_DEFINITIONS.applicantEmail.csvColumnName],
        applicantPhone: row[COLUMN_DEFINITIONS.applicantPhone.csvColumnName],
        applicantName: row[COLUMN_DEFINITIONS.applicantName.csvColumnName],
        caseNumber: row[COLUMN_DEFINITIONS.caseNumber.csvColumnName],
        caseCategory: row[COLUMN_DEFINITIONS.category.csvColumnName],
        applicantTags: prepareTags(row[COLUMN_DEFINITIONS.applicantTags.csvColumnName] ?? ''),
        caseTags: prepareTags(row[COLUMN_DEFINITIONS.caseTags.csvColumnName] ?? ''),
      })),
      {
        onSuccess: async (data) => {
          await queryClient.invalidateQueries({
            queryKey: searchApplicantQueryKey.base,
          })
          setResult(data)
          toast.success(`${data.successCount} row${data.successCount === 1 ? '' : 's'} imported.`)
        },
        onError: (e) => {
          if (isWretchError(e) && e.status === 900) {
            toast.error(
              "You've reached the maximum number of cases for your current plan. Please upgrade to add more cases."
            )
          } else {
            toast.error('An error occurred during the import process.')
          }
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  return (
    <div>
      <ImportContentHeader />

      {!dataUser?.hasSubscription && (
        <div
          className={cn(
            'flex flex-col items-center space-y-5',
            'rounded-[10px]',
            'border border-slate-200',
            'bg-[#f9f9fb]',
            'p-6'
          )}
        >
          <img src={paperGraphicImage} alt="paper-graphic" />
          <div className={cn('max-w-[670px]')}>
            <NudgingSubscriptionBanner
              isAdmin={dataUser?.isAdmin}
              isFreeTrialAvailable={dataUser?.isFreeTrialAvailable}
            />
          </div>
        </div>
      )}

      {dataUser?.hasSubscription && (
        <>
          <ImportFileInput file={hasError || rows.length === 0 ? null : file} onSelect={setFile} />
          {nInvalidRows > 0 && nInvalidRows === rows.length ? (
            <div className={cn('mt-5', 'text-sm text-red-500')}>
              Invalid file format. Please ensure your file matches the provided example and try again.
            </div>
          ) : hasError ? (
            <div className={cn('mt-5', 'text-sm text-red-500')}>
              An error occurred during the import process. Please check your file and try again.
            </div>
          ) : rows.length > 0 ? (
            <>
              <ImportPreview rows={rows} />
              <ImportConfirm
                nInvalidRows={nInvalidRows}
                result={result}
                submitting={mutation.isPending}
                onSubmit={handleSubmit}
              />
              {!!result && <ImportResult result={result} />}
            </>
          ) : null}
        </>
      )}
    </div>
  )
}
